import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import type { ElementType, DragEvent } from 'react';
import toast from 'react-hot-toast';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import ObjectBrowser, { type ObjectBrowserHandle } from './ObjectBrowser';
import BucketAccessPanel from './BucketAccessPanel';
import BucketPlanInfo from './BucketPlanInfo';
import {
  XMarkIcon, FolderIcon, KeyIcon, Cog6ToothIcon,
  GlobeAltIcon, LockClosedIcon, ClipboardDocumentIcon,
  ChevronDownIcon, ChevronUpIcon,
} from '@heroicons/react/24/outline';
import type { StorageBucket } from '../../types/objectStorage';
import { copyToClipboard } from '../../utils/clipboard';
import { formatBytes } from '../../utils/formatBytes';
import { objectStorageService } from '../../services/objectStorageService';

class TabBoundary extends React.Component<{ children: React.ReactNode }, { err: Error | null }> {
  state = { err: null as Error | null };
  static getDerivedStateFromError(err: Error) { return { err }; }
  render() {
    if (this.state.err) {
      return (
        <div className="rounded border border-red-500/40 bg-red-500/5 p-4 text-sm">
          <p className="text-red-300 mb-2">Something broke loading this tab.</p>
          <button
            onClick={() => this.setState({ err: null })}
            className="px-2 py-1 rounded bg-gray-800 hover:bg-gray-700 text-gray-200"
          >
            Retry
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

export type BucketModalTab = 'files' | 'access' | 'settings';

export function isDeleteConfirmed(name: string, typed: string): boolean {
  return typed.length > 0 && typed === name;
}

interface Props {
  bucket: StorageBucket;
  projectId: string;
  initialTab?: BucketModalTab;
  onClose: () => void;
  onUpdated?: () => void;
}

const TABS: { id: BucketModalTab; label: string; icon: ElementType }[] = [
  { id: 'files',    label: 'Files',    icon: FolderIcon },
  { id: 'access',   label: 'Access',   icon: KeyIcon },
  { id: 'settings', label: 'Settings', icon: Cog6ToothIcon },
];

export default function BucketModal({ bucket, projectId, initialTab = 'files', onClose, onUpdated }: Props) {
  const [tab, setTab] = useState<BucketModalTab>(initialTab);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [onClose]);

  return createPortal(
    <div className="fixed inset-0 z-[100] bg-black/70 flex md:items-center md:justify-center" onClick={onClose}>
      <div
        onClick={(e) => e.stopPropagation()}
        className="
          bg-gray-900 border border-gray-700 text-white flex flex-col
          w-full h-full
          md:rounded-xl md:max-w-4xl md:h-auto md:max-h-[90vh] md:w-[90vw]
        "
      >
        {/* Persistent header */}
        <BucketHeader bucket={bucket} onClose={onClose} />

        {/* Tab strip (desktop top, mobile bottom) */}
        <div className="hidden md:flex border-b border-gray-800">
          {TABS.map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`flex items-center gap-2 px-4 py-2 text-sm ${
                tab === t.id ? 'text-white border-b-2 border-primary-500' : 'text-gray-400 hover:text-white'
              }`}
            >
              <t.icon className="w-4 h-4" />
              {t.label}
            </button>
          ))}
        </div>

        {/* Tab body */}
        <div className="flex-1 overflow-y-auto p-4 md:p-5">
          <TabBoundary>
            {tab === 'files' && <FilesTab projectId={projectId} bucket={bucket} />}
          </TabBoundary>
          <TabBoundary>
            {tab === 'access' && <BucketAccessPanel projectId={projectId} bucketId={bucket.id} />}
          </TabBoundary>
          <TabBoundary>
            {tab === 'settings' && (
              <SettingsTab bucket={bucket} projectId={projectId} onClose={onClose} onUpdated={onUpdated} />
            )}
          </TabBoundary>
        </div>

        {/* Mobile bottom tab bar */}
        <div className="md:hidden border-t border-gray-800 grid grid-cols-3 pb-[env(safe-area-inset-bottom)]">
          {TABS.map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`flex flex-col items-center justify-center py-2 text-[11px] ${
                tab === t.id ? 'text-primary-300' : 'text-gray-400'
              }`}
            >
              <t.icon className="w-5 h-5 mb-0.5" />
              {t.label}
            </button>
          ))}
        </div>

      </div>
    </div>,
    document.body,
  );
}

function SettingsTab({
  bucket, projectId, onClose, onUpdated,
}: { bucket: StorageBucket; projectId: string; onClose: () => void; onUpdated?: () => void }) {
  const qc = useQueryClient();
  const [versioning, setVersioning] = useState(bucket.versioning);
  const [isPublic,   setIsPublic]   = useState(bucket.is_public);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [typedName, setTypedName] = useState('');

  const updateMutation = useMutation({
    mutationFn: (data: { versioning: boolean; is_public: boolean }) =>
      objectStorageService.updateBucket(projectId, bucket.id, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['buckets', projectId] });
      onUpdated?.();
    },
    onError: () => {
      // Revert UI state on failure
      setVersioning(bucket.versioning);
      setIsPublic(bucket.is_public);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: () => objectStorageService.deleteBucket(projectId, bucket.id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['buckets', projectId] });
      onClose();
    },
    onError: () => toast.error('Failed to delete bucket. Please try again.'),
  });

  const totalSizeLabel = formatBytes(bucket.current_size_bytes ?? 0);

  return (
    <div className="space-y-4 text-sm">
      {/* Card 1: bucket configuration */}
      <section className="rounded-lg border border-gray-800 bg-gray-900/40 p-4 space-y-3">
        <p className="font-medium text-white">Bucket configuration</p>
        <ToggleRow
          label="Versioning"
          help="Keep historical copies of objects when overwritten."
          checked={versioning}
          onChange={(v) => { setVersioning(v); updateMutation.mutate({ versioning: v, is_public: isPublic }); }}
          disabled={updateMutation.isPending}
        />
        <ToggleRow
          label="Public access"
          help="Allow anonymous GET/LIST on this bucket."
          checked={isPublic}
          onChange={(v) => { setIsPublic(v); updateMutation.mutate({ versioning, is_public: v }); }}
          disabled={updateMutation.isPending}
        />
      </section>

      {/* Card 2: plan & usage */}
      <section className="rounded-lg border border-gray-800 bg-gray-900/40 p-4 space-y-2">
        <p className="font-medium text-white">Plan & usage</p>
        <BucketPlanInfo bucket={bucket} projectId={projectId} variant="embedded" />
      </section>

      {/* Card 3: danger zone */}
      <section className="rounded-lg border border-red-500/40 bg-red-500/5 p-4 space-y-3">
        <p className="font-medium text-red-300">Danger zone</p>
        {!confirmDelete ? (
          <button
            type="button"
            onClick={() => setConfirmDelete(true)}
            className="px-3 py-1.5 rounded border border-red-500/60 text-red-300 hover:bg-red-500/10"
          >
            Delete bucket
          </button>
        ) : (
          <div className="space-y-2">
            <p className="text-red-200 text-xs">
              This bucket has {(bucket.object_count ?? 0).toLocaleString()} objects ({totalSizeLabel}).
              Deleting will permanently remove all of them. Type the bucket name to confirm.
            </p>
            <input
              autoFocus
              value={typedName}
              onChange={(e) => setTypedName(e.target.value)}
              placeholder={bucket.name}
              className="w-full px-2 py-1.5 bg-gray-900 border border-red-500/40 rounded font-mono text-sm text-white"
            />
            <div className="flex gap-2">
              <button
                type="button"
                disabled={!isDeleteConfirmed(bucket.name, typedName) || deleteMutation.isPending}
                onClick={() => deleteMutation.mutate()}
                className="px-3 py-1.5 rounded bg-red-500 hover:bg-red-600 text-white disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {deleteMutation.isPending ? 'Deleting…' : 'Permanently delete'}
              </button>
              <button
                type="button"
                onClick={() => { setConfirmDelete(false); setTypedName(''); }}
                className="px-3 py-1.5 rounded bg-gray-800 hover:bg-gray-700 text-gray-200"
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </section>
    </div>
  );
}

function ToggleRow({
  label, help, checked, onChange, disabled,
}: { label: string; help: string; checked: boolean; onChange: (v: boolean) => void; disabled?: boolean }) {
  return (
    <div className="flex items-start justify-between gap-3">
      <div>
        <p className="text-sm text-white">{label}</p>
        <p className="text-[11px] text-gray-400">{help}</p>
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        aria-label={label}
        disabled={disabled}
        onClick={() => onChange(!checked)}
        className={`relative w-10 h-5 rounded-full transition-colors shrink-0 disabled:opacity-50 disabled:cursor-not-allowed ${checked ? 'bg-primary-500' : 'bg-gray-600'}`}
      >
        <span className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${checked ? 'translate-x-5' : ''}`} />
      </button>
    </div>
  );
}

function FilesTab({ projectId, bucket }: { projectId: string; bucket: StorageBucket }) {
  const [dragging, setDragging] = useState(false);
  const browserRef = useRef<ObjectBrowserHandle>(null);

  const onDragEnter = (e: DragEvent<HTMLDivElement>) => {
    if (e.dataTransfer.types.includes('Files')) {
      e.preventDefault();
      setDragging(true);
    }
  };
  const onDragLeave = (e: DragEvent<HTMLDivElement>) => {
    if (!e.currentTarget.contains(e.relatedTarget as Node)) {
      setDragging(false);
    }
  };
  const onDrop = async (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragging(false);
    if (e.dataTransfer.files?.length) {
      await browserRef.current?.uploadFiles(e.dataTransfer.files);
    }
  };

  return (
    <div
      className="relative min-h-full -m-4 md:-m-5 p-4 md:p-5"
      onDragEnter={onDragEnter}
      onDragOver={(e) => { if (e.dataTransfer.types.includes('Files')) e.preventDefault(); }}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
    >
      <ObjectBrowser ref={browserRef} projectId={projectId} bucket={bucket} embedded />
      {dragging && (
        <div className="absolute inset-2 rounded-lg border-2 border-dashed border-primary-400 bg-primary-500/10 flex items-center justify-center pointer-events-none">
          <p className="text-primary-200 text-sm">Drop files to upload to the current folder</p>
        </div>
      )}
    </div>
  );
}

function BucketHeader({ bucket, onClose }: { bucket: StorageBucket; onClose: () => void }) {
  const [s3Open, setS3Open] = useState(false);
  const quotaBytes = (bucket.plan_storage_gb ?? 0) * 1024 * 1024 * 1024;
  const used = bucket.current_size_bytes ?? 0;
  const pct = quotaBytes > 0 ? Math.min(100, (used / quotaBytes) * 100) : 0;

  // TODO: derive from bucket.endpoint when multi-region lands. Hardcoded
  // to the only region available today (ap-southeast-1).
  const endpointHost = 'ind-dc01-jsr.bucket.podstack.ai';
  const endpoint = `https://${endpointHost}`;

  return (
    <div className="px-4 md:px-5 pt-3 md:pt-4 pb-3 border-b border-gray-800 space-y-3">
      {/* Row 1: title + chips + close */}
      <div className="flex items-start gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="text-base md:text-lg font-semibold truncate" title={bucket.name}>{bucket.name}</p>
            <span className="text-[10px] px-1.5 py-0.5 rounded bg-gray-800 text-gray-300 border border-gray-700">
              {bucket.region}
            </span>
            {bucket.is_public ? (
              <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full bg-amber-500/10 text-amber-300 border border-amber-500/30">
                <GlobeAltIcon className="w-3 h-3" /> Public
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full bg-gray-700/60 text-gray-300 border border-gray-600">
                <LockClosedIcon className="w-3 h-3" /> Private
              </span>
            )}
          </div>
        </div>
        <button onClick={onClose} className="p-1 -m-1 rounded hover:bg-gray-800 shrink-0" aria-label="Close">
          <XMarkIcon className="w-5 h-5" />
        </button>
      </div>

      {/* Row 2: stats */}
      <div className="flex flex-wrap items-center gap-3 text-xs">
        <div className="flex items-center gap-2 min-w-[160px]">
          <span className="text-gray-400">Used</span>
          <span className="font-mono text-gray-200">
            {formatBytes(used)}{quotaBytes > 0 ? ` / ${formatBytes(quotaBytes)}` : ''}
          </span>
          {quotaBytes > 0 && (
            <div className="flex-1 h-1.5 bg-gray-800 rounded overflow-hidden min-w-[60px]">
              <div className="h-full bg-primary-500" style={{ width: `${pct}%` }} />
            </div>
          )}
        </div>
        <div className="flex items-center gap-1 text-gray-400">
          Objects: <span className="font-mono text-gray-200">{(bucket.object_count ?? 0).toLocaleString()}</span>
        </div>
        {bucket.plan_id && (
          <div className="text-gray-400">
            Plan: <span className="text-gray-200">{bucket.plan_id}</span>
          </div>
        )}
      </div>

      {/* Row 3: S3 connection chips (collapsible) */}
      <div>
        <button
          onClick={() => setS3Open((o) => !o)}
          className="text-[11px] text-primary-300 hover:text-primary-200 flex items-center gap-1"
        >
          {s3Open ? <ChevronUpIcon className="w-3 h-3" /> : <ChevronDownIcon className="w-3 h-3" />}
          S3 connection
        </button>
        {s3Open && (
          <div className="mt-2 flex flex-wrap gap-2 text-[11px]">
            <Chip label="endpoint" value={endpoint} onCopy={copyToClipboard} />
            <Chip label="region"   value={bucket.region} onCopy={copyToClipboard} />
            {bucket.bucket_key && (
              <Chip label="bucket_key" value={bucket.bucket_key} onCopy={copyToClipboard} />
            )}
          </div>
        )}
      </div>

      {/* Row 4: public URL */}
      {bucket.is_public && bucket.public_url && (
        <div className="flex items-center gap-2 text-xs">
          <span className="text-gray-400">Public URL</span>
          <code className="flex-1 truncate bg-gray-950 px-2 py-1 rounded font-mono text-primary-300 border border-gray-800">
            {bucket.public_url}
          </code>
          <button onClick={() => copyToClipboard(bucket.public_url!)} className="px-2 py-1 bg-gray-800 hover:bg-gray-700 rounded">
            <ClipboardDocumentIcon className="w-4 h-4 text-white" />
          </button>
        </div>
      )}
    </div>
  );
}

function Chip({ label, value, onCopy }: { label: string; value: string; onCopy: (s: string) => void }) {
  return (
    <button
      onClick={() => onCopy(value)}
      className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full bg-gray-800 hover:bg-gray-700 border border-gray-700"
      title={`Copy ${label}`}
    >
      <span className="text-gray-400">{label}</span>
      <span className="font-mono text-gray-200 max-w-[180px] truncate">{value}</span>
      <ClipboardDocumentIcon className="w-3 h-3 text-gray-400" />
    </button>
  );
}
